/**
 * [TD-73 Section 2.2] Per-user audio preferences in localStorage.
 *
 * Key shape: `pgf_audio_prefs:${userId}` → JSON object.
 *
 * Schema (locked v1):
 *   {
 *     version: 1,
 *     music_enabled: boolean,
 *     music_volume: 0..1,
 *     tts_enabled: boolean,
 *     tts_volume: 0..1,
 *     first_lobby_visit_completed: boolean,
 *     updated_at: ISO8601 string
 *   }
 *
 * Drives:
 *   - DraftLobby autoplay decision (resolveLobbyAutoplay).
 *   - DraftRoom mute/volume hydration (Gap G.1).
 *
 * Defensive against:
 *   - Missing key (returns null from loadAudioPrefs).
 *   - Corrupt JSON (returns null; caller treats as no-prefs).
 *   - Missing user id (no-op; localStorage namespace not pollutable).
 */

const STORAGE_PREFIX = "pgf_audio_prefs:";

const DEFAULTS = Object.freeze({
  version: 1,
  music_enabled: true,
  music_volume: 0.4,
  tts_enabled: true,
  tts_volume: 0.85,
  first_lobby_visit_completed: false,
  updated_at: null,
});

function _storageKey(userId) {
  if (!userId) return null;
  return `${STORAGE_PREFIX}${userId}`;
}

export function loadAudioPrefs(userId) {
  const key = _storageKey(userId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // Shallow-merge defaults so future schema additions don't crash old prefs.
    return { ...DEFAULTS, ...parsed };
  } catch {
    return null;
  }
}

export function saveAudioPrefs(userId, patch) {
  const key = _storageKey(userId);
  if (!key) return;
  const current = loadAudioPrefs(userId) || { ...DEFAULTS };
  const next = {
    ...current,
    ...patch,
    updated_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Quota exceeded / disabled storage - swallow. Prefs are not load-bearing.
  }
}

/**
 * [TD-73 E.3-modified] Resolves the lobby-mount autoplay decision.
 *
 * Returns: { shouldPlay: boolean, volume: number, isFirstVisit: boolean }
 *
 * Side-effect: on first visit (no prefs OR first_lobby_visit_completed=false),
 * writes `first_lobby_visit_completed: true` + seeds defaults so subsequent
 * lobby mounts respect the user's choices.
 */
export function resolveLobbyAutoplay(userId) {
  const prefs = loadAudioPrefs(userId);
  const isFirstVisit = !prefs || prefs.first_lobby_visit_completed === false;

  if (isFirstVisit) {
    saveAudioPrefs(userId, {
      first_lobby_visit_completed: true,
      music_enabled: true,
      music_volume: DEFAULTS.music_volume,
    });
    return {
      shouldPlay: true,
      volume: DEFAULTS.music_volume,
      isFirstVisit: true,
    };
  }

  if (prefs.music_enabled === false) {
    return { shouldPlay: false, volume: prefs.music_volume, isFirstVisit: false };
  }
  return { shouldPlay: true, volume: prefs.music_volume, isFirstVisit: false };
}

export const __AUDIO_PREFS_DEFAULTS__ = DEFAULTS; // test-only export
